/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */
import {DragDropModule} from '@angular/cdk/drag-drop';
import {CommonModule, NgOptimizedImage} from '@angular/common';
import {NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatTreeModule} from '@angular/material/tree';
import {RouterModule} from '@angular/router';
import {NbSecurityModule} from '@nebular/security';
import {
  NbActionsModule,
  NbAlertModule,
  NbAutocompleteModule,
  NbBadgeModule,
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbFormFieldModule,
  NbIconModule,
  NbInputModule,
  NbLayoutModule,
  NbListModule,
  NbMenuModule,
  NbPopoverModule,
  NbRadioModule,
  NbSelectModule,
  NbSidebarModule,
  NbSpinnerModule,
  NbTagModule,
  NbThemeModule,
  NbToastrModule,
  NbToggleModule,
  NbTooltipModule,
  NbWindowModule
} from '@nebular/theme';
import {NgSelectModule} from '@ng-select/ng-select';
import {FormlyBootstrapModule} from '@ngx-formly/bootstrap';
import {FormlyModule} from '@ngx-formly/core';
import {FormlySelectModule} from '@ngx-formly/core/select';
import {TuiIcon, TuiScrollable, TuiScrollbar} from '@taiga-ui/core';
import {
  CheckboxMultiPropertyComponent,
  DatepickerFormlyComponent,
  DatepickerRangeComponent,
  DatepickerTimeComponent,
  FileUploadComponent,
  GridGearComponent,
  ImagePreviewComponent,
  InputFilterPropertyComponent,
  IntervalWrapperComponent,
  RadioFilterPropertyComponent,
  SelectFilterPropertyComponent,
  SharedModule,
  SortPipe,
  UploaderComponent
} from 'compob2b-libs';
import {TableModule} from 'compob2b-table';
import {NgxJsonViewerModule} from 'ngx-json-viewer';
import {ACL_OPTIONS} from '../security/security';

import {AppStateToplineComponent} from './app-state-topline/app-state-topline.component';
import {ChooseCatalogModalComponent} from './choose-catalog-modal/choose-catalog-modal.component';
import {AdditionalPicturesComponent} from './crud-components/additional-pictures/additional-pictures.component';
import {CreateUpdateComponent} from './crud-components/create-update/create-update.component';
import {CertificatesUploaderComponent} from './crud-components/certificates-uploader/certificates-uploader.component';
import {DocumentsUploaderComponent} from './crud-components/documents-uploader/documents-uploader.component';
import {ListActionsComponent} from './crud-components/list/list-actions/list-actions.component';
import {ListComponent} from './crud-components/list/list.component';
import {ManufacturesComponent} from './crud-components/manufactures/manufactures.component';
import {PictureComponent} from './crud-components/picture/picture.component';
import {SearchRangingRelationComponent} from './crud-components/search-ranging-relation/search-ranging-relation.component';
import {TermsByIdComponent} from './crud-components/terms-by-id/terms-by-id.component';
import {TermsComponent} from './crud-components/terms/terms.component';
import {YoutubeVideosComponent} from './crud-components/youtube-videos/youtube-videos.component';
import {AddressWithGeoComponent} from './custom-form-types/address-with-geo/address-with-geo.component';
import {AlertPropertyComponent} from './custom-form-types/alert-property/alert-property.component';
import {AutoCompleteComponent} from './custom-form-types/auto-complete/auto-complete.component';
import {CatalogPickerMultiComponent} from './custom-form-types/catalog-picker-multi/catalog-picker-multi.component';
import {CatalogPickerComponent} from './custom-form-types/catalog-picker/catalog-picker.component';
import {ColorPickerComponent} from './custom-form-types/colorpicker/color-picker.component';
import {EditorComponent} from './custom-form-types/editor/editor.component';
import {JsonInputComponent} from './custom-form-types/json-input/json-input.component';
import {LinkPropertyComponent} from './custom-form-types/link-property/link-property.component';
import {PhoneMaskComponent} from './custom-form-types/phone-mask/phone-mask.component';
import {StringListComponent} from './custom-form-types/string-list/string-list.component';
import {ProductLinkComponent} from './custom-form-types/product-link/product-link.component';
import {SelectLazyMultiComponent} from './custom-form-types/select-lazy-multi/select-lazy-multi.component';
import {SelectLazyComponent} from './custom-form-types/select-lazy/select-lazy.component';
import {SizePropertyComponent} from './custom-form-types/size-property/size-property.component';
import {SmartlistComponent} from './custom-form-types/smartlist/smartlist.component';
import {FeatureMapperComponent} from './custom-form-types/template-mapper/modals/feature-mapper/feature-mapper.component';
import {
  TemplateFeatureValueMapperComponent
} from './custom-form-types/template-mapper/modals/template-feature-value-mapper/template-feature-value-mapper.component';
import {TemplateMapperComponent} from './custom-form-types/template-mapper/template-mapper.component';
import {YoutubePreviewComponent} from './custom-form-types/youtube-preview/youtube-preview.component';
import {LayoutComponent} from './layout/layout.component';
import {PopupAddressComponent} from './modal-components/popup-address/popup-address.component';
import {ModalMenuComponent} from './modal-menu/modal-menu.component';
import {AppCreateUpdateFormComponent} from "./crud-components/create-update/app-create-update-form/app-create-update-form.component";
import {ListColumnsComponent} from './crud-components/list/list-columns/list-columns.component'
import {CreateUpdateHeaderComponent} from './crud-components/create-update/create-update-header/create-update-header.component'
import {MenuSearch} from './layout/menu-search/menu-search.component'
import {SeoFieldHelpWrapperComponent} from '../cabinet/seo/shared/seo-field-help.wrapper';
import {SeoVersionInfoComponent} from '../cabinet/seo/shared/seo-version-info.type';

@NgModule({
  declarations: [
    CreateUpdateHeaderComponent,
    AdditionalPicturesComponent,
    AddressWithGeoComponent,
    AlertPropertyComponent,
    AppStateToplineComponent,
    AutoCompleteComponent,
    CatalogPickerComponent,
    CatalogPickerMultiComponent,
    CertificatesUploaderComponent,
    ChooseCatalogModalComponent,
    CreateUpdateComponent,
    DocumentsUploaderComponent,
    FeatureMapperComponent,
    JsonInputComponent,
    LayoutComponent,
    LinkPropertyComponent,
    ListComponent,
    ManufacturesComponent,
    ModalMenuComponent,
    PhoneMaskComponent,
    PictureComponent,
    PopupAddressComponent,
    ProductLinkComponent,
    SearchRangingRelationComponent,
    SelectLazyComponent,
    AppCreateUpdateFormComponent,
    SelectLazyMultiComponent,
    SeoFieldHelpWrapperComponent,
    SeoVersionInfoComponent,
    SizePropertyComponent,
    SmartlistComponent,
    TemplateFeatureValueMapperComponent,
    TemplateMapperComponent,
    TermsByIdComponent,
    TermsComponent,
    YoutubePreviewComponent,
    YoutubeVideosComponent,
  ],
  imports: [
    CommonModule,
    NbLayoutModule,
    NbIconModule,
    NbActionsModule,
    NbListModule,
    SharedModule,
    NgSelectModule,
    NbSecurityModule.forRoot(ACL_OPTIONS),
    NbSidebarModule.forRoot(),
    NbMenuModule.forRoot(),
    NbWindowModule.forRoot(),
    NbThemeModule,
    NbMenuModule,
    NbPopoverModule,
    RouterModule,
    NbButtonModule,
    NbTooltipModule,
    ReactiveFormsModule,
    NbInputModule,
    NbSelectModule,
    DragDropModule,
    NbToastrModule.forRoot(),
    FormlyBootstrapModule,
    FormlyModule.forRoot({
      wrappers: [
        {name: 'interval-wrapper', component: IntervalWrapperComponent},
        {name: 'seo-field-help', component: SeoFieldHelpWrapperComponent},
      ],
      types: [
        {name: 'address-with-geo', component: AddressWithGeoComponent},
        {name: 'alert-property', component: AlertPropertyComponent},
        {name: 'autocomplete', component: AutoCompleteComponent},
        {name: 'catalog-picker', component: CatalogPickerComponent},
        {name: 'catalog-picker-multi', component: CatalogPickerMultiComponent},
        {name: 'color-picker-input', component: ColorPickerComponent},
        {name: 'date-range-picker', component: DatepickerRangeComponent},
        {name: 'date-timepicker-input', component: DatepickerTimeComponent},
        {name: 'date', component: DatepickerFormlyComponent},
        {name: 'editor-input', component: EditorComponent},
        {name: 'file-upload-input', component: FileUploadComponent},
        {name: 'input-filter-property', component: InputFilterPropertyComponent},
        {name: 'json-input', component: JsonInputComponent},
        {name: 'link-property', component: LinkPropertyComponent},
        {name: 'checkbox-multi-property', component: CheckboxMultiPropertyComponent},
        {name: 'phone-mask-input', component: PhoneMaskComponent},
        {name: 'product-link', component: ProductLinkComponent},
        {name: 'radio-filter-property', component: RadioFilterPropertyComponent},
        {name: 'select-filter-property', component: SelectFilterPropertyComponent},
        {name: 'select-lazy', component: SelectLazyComponent},
        {name: 'select-lazy-multi', component: SelectLazyMultiComponent},
        {name: 'select-multi-filter-property', component: SelectFilterPropertyComponent},
        {name: 'seo-version-info', component: SeoVersionInfoComponent},
        {name: 'size-property', component: SizePropertyComponent},
        {name: 'string-list', component: StringListComponent},
        {name: 'smartlist', component: SmartlistComponent},
        {name: 'template-mapper', component: TemplateMapperComponent},
        {name: 'youtube-preview', component: YoutubePreviewComponent},
      ],
      validationMessages: SharedModule.validationMessages(),
    }),
    FormlySelectModule,
    NbCardModule,
    NbToggleModule,
    TableModule,
    NbSpinnerModule,
    NbBadgeModule,
    NbCheckboxModule,
    NbAutocompleteModule,
    NgxJsonViewerModule,
    NbRadioModule,
    NbAlertModule,
    FormsModule,
    NbTagModule,
    MatTreeModule,
    MatButtonModule,
    TuiScrollbar, TuiScrollable, GridGearComponent, ListActionsComponent, FormlyModule, NbFormFieldModule, ImagePreviewComponent, SortPipe, NgOptimizedImage, ListColumnsComponent, MenuSearch, UploaderComponent, NbSidebarModule, TuiIcon,
  ],
  exports: [
    PictureComponent,
    ListComponent,
    AdditionalPicturesComponent,
    CertificatesUploaderComponent,
    DocumentsUploaderComponent,
    TermsComponent,
    TermsByIdComponent,
    ManufacturesComponent,
    ProductLinkComponent,
    YoutubeVideosComponent,
    AutoCompleteComponent,
    SearchRangingRelationComponent,
    AppCreateUpdateFormComponent,
    CreateUpdateHeaderComponent,
    ModalMenuComponent,
    GridGearComponent,
    ListActionsComponent,
    ListColumnsComponent,
  ]
})
export class AdminSharedModule {
}
